import React, { useState } from "react";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { Tooltip } from "@material-ui/core";
import { Typography } from "@mui/material";

// Improved color palette with better accessibility and visual hierarchy
const getColorScheme = (level) => {
  const colorSchemes = [
    {
      background: "#f8fafc", // Very light blue-gray
      hover: "#e2e8f0",
      selected: "#dbeafe",
      border: "#e5e7eb",
      text: "#1f2937"
    },
    {
      background: "#f0f9ff", // Light blue
      hover: "#e0f2fe",
      selected: "#bae6fd",
      border: "#cbd5e1",
      text: "#0f172a"
    },
    {
      background: "#ecfdf5", // Light green
      hover: "#d1fae5",
      selected: "#a7f3d0",
      border: "#d1d5db",
      text: "#064e3b"
    },
    {
      background: "#fef7ff", // Light purple
      hover: "#fae8ff",
      selected: "#e9d5ff",
      border: "#d1d5db",
      text: "#581c87"
    },
    {
      background: "#fff7ed", // Light orange
      hover: "#fed7aa",
      selected: "#fdba74",
      border: "#d1d5db",
      text: "#9a3412"
    }
  ];
  
  // Cycle through color schemes for different levels
  return colorSchemes[level % colorSchemes.length];
};

function Row({ headers, data, selected, setSelected, setSelectedData, level = 0 }) {
  const [open, setOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const colorScheme = getColorScheme(level);

  const onRowClick = () => {
    console.log("data----", data);
    if (selected === data.id) {
      setSelected(0);
    } else {
      setSelectedData(data);
      setSelected(data.id);
    }
  };

  const getRowBackgroundColor = () => {
    if (selected === data.id) return colorScheme.selected;
    if (isHovered) return colorScheme.hover;
    return colorScheme.background;
  };

  return (
    <React.Fragment>
      <TableRow
        sx={{
          "& > *": {
            borderBottom: `1px solid ${colorScheme.border}`,
            backgroundColor: getRowBackgroundColor(),
            color: colorScheme.text,
            transition: "background-color 0.2s ease-in-out",
          },
          cursor: "pointer",
          "&:hover": {
            "& > *": {
              backgroundColor: selected === data.id ? colorScheme.selected : colorScheme.hover,
            }
          }
        }}
        onClick={onRowClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {data.children ? (
          <TableCell
            align="center"
            className="table-border"
            sx={{
              width: 50,
              p: 1,
              borderRight: `1px solid ${colorScheme.border}`,
            }}
          >
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(!open);
              }}
              sx={{
                color: colorScheme.text,
                "&:hover": {
                  backgroundColor: `${colorScheme.border}40`,
                }
              }}
            >
              {open ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
            </IconButton>
          </TableCell>
        ) : (
          <TableCell
            className="table-border-blank-box"
            sx={{
              width: 50,
              p: 1,
              borderRight: `1px solid ${colorScheme.border}`,
            }}
          >
            <IconButton className="button-h-w-32" size="small" disabled />
          </TableCell>
        )}
        {headers.map((h, i) => (
          <TableCellWrapper
            key={i}
            def={h}
            text={
              h.valueFormatter ? h.valueFormatter(data[h.field]) : data[h.field]
            }
            colorScheme={colorScheme}
          />
        ))}
      </TableRow>
      <TableRow>
        <TableCell
          style={{ padding: 0 }}
          colSpan={12}
          className="table-column-2"
          sx={{
            backgroundColor: colorScheme.background,
            borderBottom: 0,
          }}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box 
              className="report-box-shadow" 
              sx={{ 
                m: 0, 
                p: 0,
                borderLeft: `3px solid ${colorScheme.border}`,
                marginLeft: 2,
              }}
            >
              <CollapsibleTable
                rows={data.children === null ? [] : data.children}
                headers={headers}
                showHeader={false}
                selected={selected}
                setSelected={setSelected}
                setSelectedData={setSelectedData}
                level={level + 1}
              />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

function TableCellWrapper({ def, text, colorScheme }) {
  const { minWidth, maxWidth, needsTooltip } = def;
  return (
    <TableCell
      align="center"
      className="table-column-border width-100"
      sx={{
        p: "7px",
        borderRight: `1px solid ${colorScheme.border}`,
        color: colorScheme.text,
      }}
    >
      {needsTooltip ? (
        <Tooltip title={text}>
          <Box
            component="span"
            sx={{ 
              minWidth, 
              maxWidth, 
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
              display: "inline-block",
              width: "100%"
            }}
          >
            <Typography
              sx={{
                fontSize: "0.875rem",
                fontWeight: 500,
                color: colorScheme.text,
              }}
            >
              {text}
            </Typography>
          </Box>
        </Tooltip>
      ) : (
        <Box
          component="span"
          sx={{ 
            minWidth, 
            maxWidth, 
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
            display: "inline-block",
            width: "100%"
          }}
        >
          <Typography
            sx={{
              fontSize: "0.875rem",
              fontWeight: 500,
              color: colorScheme.text,
            }}
          >
            {text}
          </Typography>
        </Box>
      )}
    </TableCell>
  );
}

export default function CollapsibleTable({
  headers = [],
  rows = [],
  showHeader = true,
  selected,
  setSelected,
  setSelectedData,
  level = 0,
}) {
  const colorScheme = getColorScheme(level);
  
  return (
    <TableContainer
      component={Paper}
      sx={{ 
        width: "100%", 
        overflowX: "auto",
        boxShadow: level === 0 ? "0 4px 6px -1px rgba(0, 0, 0, 0.1)" : "none",
        borderRadius: level === 0 ? "8px" : "0px",
      }}
      className="br-0"
    >
      <Table
        aria-label="collapsible table"
        className="project-view-table"
        sx={{ 
          minWidth: 700, 
          maxWidth: "100%",
          backgroundColor: colorScheme.background,
        }}
      >
        {showHeader ? (
          <TableHead className="table-head">
            <TableRow 
              className="table-row"
              sx={{
                backgroundColor: "#374151", // Dark gray header
                "& th": {
                  color: "#ffffff",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }
              }}
            >
              <TableCell 
                className="table-border width-50" 
                sx={{
                  backgroundColor: "#374151",
                  borderRight: "1px solid #4b5563",
                }}
              />
              {headers.map((h, i) => (
                <TableCell
                  className="table-border width-100"
                  align="center"
                  key={i}
                  sx={{
                    p: 1,
                    backgroundColor: "#374151",
                    borderRight: i < headers.length - 1 ? "1px solid #4b5563" : "none",
                    color: "#ffffff",
                  }}
                >
                  {h.headerName}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
        ) : (
          <></>
        )}
        <TableBody
          className="table-body"
          sx={{ 
            backgroundColor: colorScheme.background,
            "& tr:last-child td": {
              borderBottom: 0,
            }
          }}
        >
          {rows.map((row, i) => (
            <Row
              align="center"
              key={i}
              headers={headers}
              data={row}
              selected={selected}
              setSelected={setSelected}
              setSelectedData={setSelectedData}
              level={level}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}